import { Button, Markdown } from "@procurement-agents/ui";
import { ApiError, isProcurementView, type PreviewInfo, type RunViewProcurement } from "@procurement-agents/scope-api";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useScopeApi } from "../api";
import { activityLabel, relativeTime } from "../labels";
import { DraftGate, useAgents } from "./AgentsApp";

const POLL_MS = 2000;

/** Run the whole pipeline against a golden case with the draft (SPEC_SLICE6 section 6), watch it, then read the comparison. */
export function Preview() {
  const { api, toast } = useScopeApi();
  const { overview, draft, refresh } = useAgents();
  const [caseId, setCaseId] = useState(overview.cases.includes("001_cleaning_services") ? "001_cleaning_services" : overview.cases[0] ?? "");
  const [run, setRun] = useState<RunViewProcurement | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [last, setLast] = useState<PreviewInfo | null>(null);
  const timer = useRef<number | null>(null);
  const records = draft?.preview_run_ids ?? [];
  const latest = [...records].reverse().find((r) => r.status === "complete");

  const poll = useCallback(async (id: string) => {
    try {
      const r = await api.getRun(id);
      if (!isProcurementView(r)) return;
      setRun(r);
      if (r.status === "running" || r.status === "waiting") timer.current = window.setTimeout(() => void poll(id), POLL_MS);
      else { await refresh(); if (r.preview) setLast(r.preview); }
    } catch (e) { if (e instanceof ApiError) toast(e.detail, "warn"); }
  }, [api, refresh, toast]);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  useEffect(() => {   // the estimate: the last complete preview's model calls
    if (!latest || runId) return;
    api.getRun(latest.run_id).then((r) => { if (isProcurementView(r) && r.preview) setLast(r.preview); }).catch(() => {});
  }, [api, latest, runId]);

  const start = async () => {
    try {
      const r = await api.previewAgentDraft(caseId);
      setRunId(r.run_id); setRun(null);
      toast(`Preview started against ${r.case}.`);
      timer.current = window.setTimeout(() => void poll(r.run_id), 800);
    } catch (e) { toast(e instanceof ApiError ? e.detail : String(e), "warn"); }
  };
  if (!draft) return <><DraftGate /><p className="muted small">Previews run against a draft.</p></>;
  const running = !!run && (run.status === "running" || run.status === "waiting");
  const estimate = last?.result?.model_calls_total;
  return (
    <section className="preview-page">
      <div className="row wrap">
        <label className="field"><span className="field-label">Golden case</span>
          <select aria-label="Golden case for the preview" value={caseId} disabled={!overview.cases.length} onChange={(e) => setCaseId(e.target.value)}>{overview.cases.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        </label>
        <Button variant="primary" onClick={() => void start()} disabled={!overview.cases.length || running || !!runId && !run}>Run preview</Button>
        <span className="muted small">{overview.cases.length ? (estimate != null ? `The last preview used ${estimate} model calls; expect about the same.` : "Real model calls: a whole run, usually a few minutes.") : "No golden cases are installed for this tenant, so there is nothing to preview against."}</span>
      </div>
      <p className="muted small">Publishing needs a preview of exactly this draft (settings, instructions and pipeline): {draft.previewed ? <strong>done for the current content</strong> : <strong>not yet run for the current content</strong>}.</p>
      {runId && (
        <div className="preview-run" aria-live="polite">
          {running || !run ? <p className="status-line"><span className="pulse" aria-hidden /> {run ? activityLabel({ current_node: run.current_node, current_started_at: run.current_started_at, progress: run.progress }, run.pending_interrupt) : "Starting…"} <span className="muted small">phase: {run?.phase ?? "intake"}</span></p>
            : run.status === "failed" ? <p className="error">The preview failed: {run.last_error}</p>
            : <p className="small">Complete{run.cost_summary ? ` · ${run.cost_summary.model_calls_total} model calls` : ""}. <Link to={`/scope/runs/${runId}`}>Open the preview run in the reader</Link></p>}
        </div>
      )}
      {last?.result && (
        <div className="comparison">
          <h3>Against the baseline of {last.case}</h3>
          {last.result.comparison_md ? <div className="md"><Markdown text={last.result.comparison_md} /></div> : <p className="muted small">The case has no accepted baseline to compare with.</p>}
          <p className="muted small">Sections planned {last.result.metrics.sections_planned} · placeholders {last.result.metrics.placeholders} · findings {Object.entries(last.result.metrics.qa_findings).map(([k, v]) => `${k} ${v}`).join(", ")} · {last.result.model_calls_total} model calls. Folder: {last.label}. Rubric scores come from score_run.py on that folder when a grader is configured.</p>
        </div>
      )}
      {records.length > 0 && (
        <div className="history">
          <h4 className="small">Previews of this draft</h4>
          <ul>{[...records].reverse().map((r) => <li key={r.run_id} className="small">{r.case} · {r.status} · {relativeTime(r.created_at)} · {r.content_hash === draft.content_hash ? <strong>current content</strong> : <span className="muted">earlier content</span>} · <Link to={`/scope/runs/${r.run_id}`}>open</Link></li>)}</ul>
        </div>
      )}
    </section>
  );
}
